import React, { useEffect, useState } from "react";
import InvoiceHeader from "./sections/InvoiceHeader";
import CompanySection from "./sections/CompanySection";
import CustomerSection from "./sections/CustomerSection";
import LineItemsSection from "./sections/LineItemsSection";
import AdditionalDetailsSection from "./sections/AdditionalDetailsSection";
import BankSection from "./sections/BankSection";
import RecurringSection from "./sections/RecurringSection";
import ActionButtons from "./sections/ActionButtons";
import SummarySection from "./sections/SummarySection";
import {
  ICreateSalesData,
  ICustomer,
  IInventory,
  ItemInput,
} from "../../@types";
import {
  InvoiceErrors,
  validateFreeFormItemDetails,
  validateInvoiceBasicInfo,
  validateInvoiceItemDetails,
  validateInvoiceSummary,
} from "../../modules/validateInvoiceForm";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useInvoiceFormState } from "../../hooks/invoice/useInvoiceFormState";
import {
  getInvoice,
  updateProfile,
  updateToPending,
} from "../../backend-services";
import { useInvoiceCalculations } from "../../hooks/invoice/useInvoiceCalculations";
import { clearFieldError, handleErrors } from "../../utils";
import { useUserStore } from "../../state";
import {
  createDraftData,
  createInvoiceFreeformData,
} from "../../modules/invoice/invoice";
import { servicesType } from "../../constants/Subscription";
import SendInvoiceModal from "./sendInvoiceModal";
import { Button } from "../common/Button";
import useLineItems from "../../modules/lineItem";
import FreeformTable from "../Sales/create/SalesCreate/FreeformTable";
import LineItemsFreeform from "./sections/WithInventory.tsx/lineItemWithout";
import { useFreeformInvoiceFormState } from "../../hooks/invoice/useFreeformInvoiceState";
import SuccessBanner from "../common/Banner/SuccessBanner";
import ServiceFreeformLineItem from "./Service/ServiceFreeformItem";
import { useCompanyStore } from "../../state/useCompanyStore";
import ServiceType from "../services/create/ServiceType";
import { useCustomToast } from "../../utils/toastUtils";
import { useProjectHook } from "../../hooks/project/useProject";
import ButtonCTA from "../Reusable/ButtonCTA";
import { ServiceTypes } from "../../@types/invoice";

const InvoiceFreeformContainer = () => {
  const navigate = useNavigate();
  const { showToast, showToastWithProps } = useCustomToast();
  const [isAddItemModalOpen, setAddItemModalOpen] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);
  const [selectedInventory, setSelectedInventory] = useState<IInventory | null>(
    null,
  );
  const [quantities, setQuantities] = useState<{ [key: string]: string }>({});
  const [showSuccessModal, setShowSuccessModal] = React.useState(false);
  const { company, fetchCompanyProfile, isRetailBusiness, setCompany } =
    useCompanyStore();

  const { invoiceNo } = useParams<{ invoiceNo: string }>();
  const [createdInvoiceId, setCreatedInvoiceId] = React.useState("");
  const [createdEmail, setCreatedEmail] = React.useState("");
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [error, setError] = React.useState<string>("");
  const [markAsPaid, setMarkAsPaid] = React.useState(false);
  const [sendEmail, setSendEmail] = React.useState(false);
  const [shouldSendWhatsApp, setShouldSendWhatsApp] = React.useState(false);
  const [shouldSendSMS, setShouldSendSMS] = React.useState(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [inventories, setInventories] = useState([]);
  const [, setCustomer] = React.useState<ICustomer | null>(null);
  const location = useLocation();
  const [isBannerVisible, setIsBannerVisible] = useState(false);
  const [bannerMessage, setBannerMessage] = useState("");
  const [bannerLinkText, setBannerLinkText] = useState("");
  const [isDraftLoading, setIsisDraftLoading] = React.useState<boolean>(false);
  const [selectedVariation, setSelectedVariation] = useState<
    string | undefined
  >(undefined);
  const [subscriptionErrorMessage, setSubscriptionErrorMessage] =
    React.useState<string>("");
  const handleViewSelection = (inventory: IInventory) => {
    setSelectedInventory(inventory);
    setAddItemModalOpen(true);
  };
  const [serviceType, setServiceType] = useState<ServiceTypes>(
    ServiceTypes.time_based,
  );
  const {
    formData,
    setFormData,
    customer,
    handleSetCustomer,
    validationResults,
    errorMessage,
    setErrorMessage,
    isLoading,
    setIsLoading,
    showSidebar,
    setShowSidebar,
    handleInputChange,
    validateForm,
    resetForm,
    isRetail,
    setValidationResults,
  } = useFreeformInvoiceFormState();
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const customerId = searchParams.get("customerId");
    const customerLastName = searchParams.get("customerLastName");
    const customerFirstName = searchParams.get("customerFirstName");
    const customerPhone = searchParams.get("customerPhone");
    const customerBusinessName = searchParams.get("customerBusinessName");
    const invoiceType = searchParams.get("invoiceType");

    if (
      customerId &&
      ((customerLastName && customerFirstName) || customerBusinessName) &&
      invoiceType
    ) {
      setCustomer({
        id: customerId,
        lastName: customerLastName ?? "",
        firstName: customerFirstName ?? "",
        phone: customerPhone ?? "",
        businessName: customerBusinessName ?? "",
      });
      handleSetCustomer({
        id: customerId,
        lastName: customerLastName ?? "",
        firstName: customerFirstName ?? "",
        phone: customerPhone ?? "",
        businessName: customerBusinessName ?? "",
      });
    }
  }, [searchParams]);

  const setLogoUrl = async (url: string) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      logoBase64: url,
    }));
    if (!url || url.trim() === "") {
      setErrorMessage("No logo to update.");
      return;
    }

    try {
      const updatedLogo = { brandLogo: url };
      const updatedUser = await updateProfile(updatedLogo);
      setCompany(updatedUser);
      fetchCompanyProfile();
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      setErrorMessage("Unable to update profile logo.");
    }
  };

  const handleEditProfileLogo = async () => {
    if (!formData.logoBase64 || formData.logoBase64.trim() === "") {
      setErrorMessage("No logo to update.");
      return;
    }

    setIsLoading(true);
    try {
      const updatedLogo = { brandLogo: formData.logoBase64 };
      await updateProfile(updatedLogo);
      fetchCompanyProfile();
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      setErrorMessage("Unable to update profile logo.");
    }
  };
  const { subTotal, total, calcTax, calcDiscount, calcShipping } =
    useInvoiceCalculations(formData, isRetail, serviceType);

  const { items, addItem, setItems, removeItem, updateCell, updateRow } =
    useLineItems([{ id: "", name: "", rate: 0, quantity: 0 }]);

  React.useEffect(() => {
    clearFieldError("inventories", validationResults, setValidationResults);
    setFormData((prevState: ICreateSalesData) => {
      return {
        ...prevState,
        inventories: items.map((item: ItemInput) => ({
          id: item.id,
          name: item.name,
          amount: 0,
          quantity: item.quantity || 0,
          rate: item.rate || 0,
          variations: item.variation || [],
        })),
      } as ICreateSalesData;
    });
  }, [items]);

  React.useEffect(() => {
    clearFieldError("services", validationResults, setValidationResults);
    setFormData((prevState: ICreateSalesData) => {
      return {
        ...prevState,
        services: items,
      };
    });
  }, [items]);

  useEffect(() => {
    if (
      typeof formData.exchangeRate !== "number" &&
      isNaN(Number(formData.exchangeRate))
    ) {
      const serviceRest = formData.services.filter((i: any) => i.id !== "");
      setFormData((prevData) => ({
        ...prevData,
        exchangeRate: 1,
        services: serviceRest,
      }));
    }
  }, [formData.exchangeRate]);

  const processCreateInvoice = async (
    sendEmail: boolean,
    markedAsPaid: boolean,
    shouldSendWhatsApp: boolean,
    shouldSendSMS: boolean,
  ) => {
    const exchange = formData.exchangeRate ?? 1;
    try {
      setIsLoading(true);
      //   setShowCreateSidebar(false);

      const {
        invoiceDueAt,
        issueDate,
        inventories,
        services,
        discount,
        tax,
        customerId,
        isReoccurring,
        lineItems,
        projectId,
      } = formData;
      const isRetail = isRetailBusiness();
      const validInvoiceDueAt = invoiceDueAt ? new Date(invoiceDueAt) : null;
      const validIssueDate = issueDate ? new Date(issueDate) : null;

      if (!validInvoiceDueAt || isNaN(validInvoiceDueAt.getTime())) {
        throw new Error("Invalid invoice due date");
      }

      if (!validIssueDate || isNaN(validIssueDate.getTime())) {
        throw new Error("Invalid issue date");
      }
      const invoiceData: ICreateSalesData = {
        customerId: customerId,
        shouldSendEmail: sendEmail,
        isPaymentCompleted: markedAsPaid,
        shouldSendWhatsApp: shouldSendWhatsApp,
        shouldSendSMS: shouldSendSMS,
        projectId: projectId || "",
        isReoccurring: isReoccurring,
        invoiceDueAt: validInvoiceDueAt.toISOString(),
        issueDate: validIssueDate.toISOString(),
        inventories: isRetail
          ? Array.isArray(inventories) && inventories.length
            ? inventories.map(
                (i: {
                  id: string;
                  name: string;
                  rate: number;
                  quantity: number;
                  variations?: any;
                }) => ({
                  id: i.id,
                  name: i.name,
                  amount: (Number(i.quantity) * Number(i.rate)) / exchange,
                  quantity: Number(i.quantity),
                  rate: Number(i.rate) / exchange,
                  variations: i.variations ? i.variations : undefined,
                }),
              )
            : []
          : [],
        lineItems: isRetail
          ? Array.isArray(lineItems) && lineItems.length
            ? lineItems.map(
                (i: { id: any; name: any; quantity: any; rate: any }) => ({
                  id: i.id,
                  name: i.name,
                  amount: (Number(i.quantity) * Number(i.rate)) / exchange,
                  quantity: Number(i.quantity),
                  rate: Number(i.rate) / exchange,
                }),
              )
            : []
          : [],
        services: !isRetail
          ? Array.isArray(services) && services.length
            ? services
                .filter((i: any) => i.name !== "")
                .map((s: { id: ""; name: ""; duration: ""; price: "" }) => ({
                  id: s.id,
                  name: s.name,
                  duration: s.duration,
                  price:
                    typeof s.price == "object"
                      ? // @ts-ignore
                        Number(s.price[0]?.amount)
                      : Number(s.price),
                }))
            : []
          : [],
        discount: Number(discount ? discount : 0),
        tax: Number(tax ? tax : 0),
        shippingCharge: Number(formData.shippingCharge || 0),
        shippingDesc: formData.shippingDesc || "",
        isReoccurringEndDate: formData.isReoccurringEndDate || "",
        reoccurringGroupName: formData.reoccurringGroupName || "",
        status: formData.status || "pending",
        description: formData.description || "",
        additionalInformation: formData.additionalInformation || "",
        currency: formData.currency || "",
        exchangeRate: formData.exchangeRate,
        paymentMethod: formData.paymentMethod || "",
        invoiceType: "freeform",
      };

      if (customer) {
        if (invoiceNo) {
          const taxValue = formData.tax ? Number(formData.tax) / 100 : 0;
          const formattedTax = taxValue.toFixed(2);
          const updatedInvoiceData = {
            status: "pending",
            projectId: projectId || "",
            invoiceDueAt: validInvoiceDueAt.toISOString(),
            issueDate: validIssueDate.toISOString(),
            invoiceType: "freeform",
            inventories: isRetail
              ? inventories.length
                ? inventories.map(
                    (i: {
                      id: string;
                      name: string;
                      rate: number;
                      quantity: number;
                      variations?: any;
                    }) => ({
                      id: i.id,
                      name: i.name,
                      amount: Number(i.quantity) * Number(i.rate),
                      quantity: Number(i.quantity),
                      rate: Number(i.rate),
                      variations: i.variations ? i.variations : undefined,
                    }),
                  )
                : []
              : [],
            services: !isRetail
              ? services.length
                ? services.map(
                    (s: { id: ""; name: ""; duration: ""; price: "" }) => ({
                      id: s.id,
                      name: s.name,
                      duration: s.duration,
                      price: Number(s.price),
                    }),
                  )
                : []
              : [],
            discount: Number(formData.discount ? formData.discount / 100 : 0),
            tax: Number(formattedTax),
          };

          const createdUpdatedInvoice = await updateToPending({
            id: invoiceNo,
            data: updatedInvoiceData,
          });
          const createdUpdatedInvoiceId = createdUpdatedInvoice?.id;
          const createdEmail = customer.email || "";

          setCreatedInvoiceId(createdUpdatedInvoiceId);
          setCreatedEmail(createdEmail);
          setShowSuccessModal(true);
        } else {
          const createdInvoice = await createInvoiceFreeformData(
            invoiceData,
            customer,
            isRetail,
          );
          const createdInvoiceId = createdInvoice?.id;
          const createdEmail = customer.email || "";

          setCreatedInvoiceId(createdInvoiceId);
          setCreatedEmail(createdEmail);

          setBannerMessage("New Invoice Created");
          setBannerLinkText("View Invoice");
          setIsBannerVisible(true);
        }
      } else {
        console.error("No customer found");
      }

      return true;
    } catch (error: any) {
      handleErrors(error, setErrorMessage);
      setErrorMessage("An error occurred while processing the invoice.");

      if (error.response) {
        const errorMessage = error.response.data;
        console.error("Error response:", errorMessage);
        setError(errorMessage);
        setShowSubscriptionModal(true);
        setSubscriptionErrorMessage(errorMessage);
      }
      setIsLoading(false);
    }
    return false;
  };

  const handleCreateInvoice = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    try {
      const basicInfoValid = validateInvoiceBasicInfo(
        formData,
        setValidationResults,
      );

      const summaryValid = validateInvoiceSummary(
        formData,
        setValidationResults,
      );

      if (basicInfoValid && summaryValid) {
        setShowSidebar(true);
      } else {
        document.getElementById("top-of-page")?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
        setErrorMessage("Please fill out all required fields correctly.");
      }
    } catch (error) {
      showToast("Failed to create invoice. Please try again later.", "error");
    }
  };

  const onCreateInvoice = async () => {
    const res = await processCreateInvoice(
      sendEmail,
      markAsPaid,
      shouldSendWhatsApp,
      shouldSendSMS,
    );
    if (res) {
      setItems([{ id: "", name: "", rate: 0, quantity: 0 }]);
      resetForm();
      setIsBannerVisible(true);
      setShowSidebar(false);
      setIsLoading(false);
      setErrorMessage("");
    }
  };

  const handlePaymentDropDownChange = (name: string, value: string) => {
    clearFieldError(name, validationResults, setValidationResults);
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const {
    AddProjectButton,
    SelectProjectModal,
    CreateProjectSideBar,
    selectedProject,
  } = useProjectHook({
    useCustomer: true,
    customerId: formData?.customerId,
    projectId: searchParams.get("projectId"),
  });

  useEffect(() => {}, [validationResults]);

  function handleSendEmailChange(checked: boolean) {
    setSendEmail(checked);
  }
  function handleMarkAsPaidChange(event: {
    target: { checked: boolean | ((prevState: boolean) => boolean) };
  }) {
    setMarkAsPaid(event.target.checked);
  }
  function handleshouldSendWhatsAppChange(checked: boolean) {
    setShouldSendWhatsApp(checked);
  }
  function handleshouldSendSMSChange(checked: boolean) {
    setShouldSendSMS(checked);
  }
  // const handleIsReoccurringChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const target = e.target as HTMLInputElement;
  //   setFormData((prevData) => ({
  //     ...prevData,
  //     isReoccurring: target.checked,
  //   }));
  //   setIsReoccurringChecked(target.checked);
  // };

  const onSaveDraft = async (event: any) => {
    event.preventDefault();
    await processCreateDraftIInvoice();
  };

  const fetchDraftInvoice = async (invoiceNo: string) => {
    if (!invoiceNo) return;

    setLoading(true);

    try {
      const response = await getInvoice(invoiceNo);
      const draftData = response;
      // Ensure we only process the invoice if it is a draft
      if (draftData && draftData.status === "draft") {
        setFormData((prevData) => ({
          ...prevData,
          customerId: draftData.customer?.id || "",
          invoiceDueAt: draftData.invoiceDueAt
            ? new Date(draftData.invoiceDueAt).toISOString()
            : "",
          issueDate: draftData.issueDate
            ? new Date(draftData.issueDate).toISOString()
            : "",
          total: draftData.total || 0,
          currency: draftData.currency || "NGN",
          description: draftData.description || "",
          additionalInformation: draftData.additionalInformation || "",
          exchangeRate: draftData.exchangeRate || 1,
          invoiceType: draftData.invoiceType || "freeform",
          isReoccurring: draftData.isReoccurring || false,
          status: draftData.status || "draft",
        }));

        setCustomer({
          id: draftData.customer?.id || "",
          firstName: draftData.customer?.firstName || "",
          lastName: draftData.customer?.lastName || "",
          phone: draftData.customer?.phone || "",
          email: draftData.customer?.email || "",
        });

        // Handle inventory or service items
        if (draftData.invoiceType === "inventory") {
          setItems(
            draftData.inventories?.map(
              (i: {
                id: any;
                name: any;
                amount: any;
                quantity: any;
                rate: any;
                variations: any;
              }) => ({
                id: i.id,
                name: i.name,
                amount: i.amount || 0,
                quantity: Number(i.quantity) || 0,
                rate: Number(i.rate) || 0,
                variations: i.variations || [],
              }),
            ) || [],
          );
        } else if (draftData.invoiceType === "service") {
          setItems(
            draftData.services?.map(
              (service: {
                id: any;
                name: any;
                price: string | any[];
                duration: any;
              }) => ({
                id: service.id,
                name: service.name,
                price:
                  service.price?.length > 0
                    ? parseFloat(service.price[0].amount)
                    : 0,
                duration: service.duration,
              }),
            ) || [],
          );
        }
      } else {
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (invoiceNo) {
      setLoading(true);
      fetchDraftInvoice(invoiceNo);
    } else {
      setIsLoading(false);
    }
  }, [invoiceNo]);

  useEffect(() => {}, [customer, inventories]);

  const processCreateDraftIInvoice = async (e?: any) => {
    setIsisDraftLoading(true);
    setShowSidebar(false);

    const isRetail = isRetailBusiness();

    const invoiceData: ICreateSalesData = {
      customerId: customer?.id,
      description: formData.description,
      invoiceDueAt: formData?.invoiceDueAt
        ? new Date(formData?.invoiceDueAt)?.toISOString()
        : undefined,
      issueDate: formData?.issueDate
        ? new Date(formData?.issueDate)?.toISOString()
        : undefined,
      additionalInformation: formData.additionalInformation,
      discount: Number(formData.discount ? formData.discount / 100 : 0),
      tax: Number(formData.tax ? formData.tax / 100 : 0),
      isReoccurring: formData.isReoccurring,
      status: "draft",
      currency: formData.currency,
      exchangeRate: formData.exchangeRate,
      invoiceType: "freeform",
      // @ts-ignore
      reminderSettings: formData.reminderSettings,
      inventories: isRetail
        ? formData.inventories.length
          ? formData.inventories.map(
              (i: {
                id: string;
                name: string;
                rate: number;
                quantity: number;
                variations?: any;
              }) => ({
                id: i.id,
                name: i.name,
                amount: 0,
                quantity: Number(i.quantity),
                rate: Number(i.rate),
                variations: i.variations ? i.variations : undefined,
              }),
            )
          : []
        : [],
      services: !isRetail
        ? formData.services.length
          ? formData.services.map(
              (s: { id: ""; name: ""; duration: ""; price: "" }) => ({
                id: s.id,
                name: s.name,
                duration: s.duration,
                price: Number(s.price),
              }),
            )
          : undefined
        : undefined,
      ...(formData.isReoccurring && {
        reoccurringGroupName: formData.reoccurringGroupName,
        isReoccurringStartDate: new Date().toISOString(),
        isReoccurringEndDate: new Date(
          formData.isReoccurringEndDate || "",
        )?.toISOString(),
      }),
    };

    try {
      const response = await createDraftData(invoiceData, customer, isRetail);
      setIsisDraftLoading(false);
      navigate("/invoices");
      showToast("Draft saved successfully!", "success");
    } catch (error: any) {
      handleErrors(error, setErrorMessage);
      setIsisDraftLoading(false);
      if (error.response) {
        const errorMessage = error.response.data;
        setShowSubscriptionModal(true);
        setSubscriptionErrorMessage(errorMessage);
      }
      const errorEl = document.getElementById("error-message");
      errorEl?.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
      document
        .getElementById("top-of-page")
        ?.scrollIntoView({ behavior: "smooth" });
      setIsLoading(false);
      setErrorMessage("An error occurred.");
    }
  };
  const { serviceData } = location.state || {};
  useEffect(() => {
    if (serviceData) {
      const ngnPrice =
        serviceData.price?.find(
          (priceItem: { amount: number; currency: string }) =>
            priceItem.currency === "NGN",
        )?.amount || 0;

      setFormData((prevFormData) => ({
        ...prevFormData,
        services: [
          {
            id: serviceData.id || "",
            name: serviceData.name || "",
            duration: serviceData.duration || "",
            price: ngnPrice,
          },
        ],
      }));
    }
  }, [serviceData]);

  // const isMobile = window.innerWidth < 768;

  // const updateReminderSettings = (updatedSettings: any) => {
  //   setFormData((prevData) => ({
  //     ...prevData,
  //     reminderSettings: updatedSettings,
  //   }));
  // };

  const handleViewInvoice = () => {
    if (createdInvoiceId) {
      const viewInvoiceRoute = `/invoices/${createdInvoiceId}/view`;
      navigate(viewInvoiceRoute);
    }
  };

  const handleCloseBanner = () => {
    setIsBannerVisible(false);
  };

  useEffect(() => {
    const validEntry = formData.inventories
      .filter((i) => i.name !== "")
      .filter((i) => i.rate != 0)
      .filter((i) => i.quantity != 0);
    let validServiceEntry;
    if (serviceType == "time-based") {
      validServiceEntry = formData.services
        .filter((i: any) => i.name !== "")
        .filter((i: any) => i.price != undefined)
        .filter((i: any) => i.duration != undefined);
    } else {
      validServiceEntry = formData.services
        .filter((i: any) => i.name !== "")
        .filter((i: any) => i.price != undefined);
    }
    setCanSubmit(
      isRetail ? validEntry.length > 0 : validServiceEntry.length > 0,
    );
  }, [formData, serviceType]);
  const [selectedCurrencyLabel, setSelectedCurrencyLabel] =
    React.useState("NGN");

  useEffect(() => {}, [formData.lineItems]);

  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      projectId: selectedProject?.id || "",
    }));
  }, [selectedProject]);
  return (
    <div id="top-of-page">
      {isBannerVisible && (
        <SuccessBanner
          message={bannerMessage}
          linkText={bannerLinkText}
          onLinkClick={handleViewInvoice}
          onClose={handleCloseBanner}
        />
      )}
      <InvoiceHeader />
      <CompanySection
        logoUrl={formData.logoBase64 || ""}
        setLogoUrl={setLogoUrl}
        error={undefined}
        onUploadSuccess={handleEditProfileLogo}
        formData={formData}
        setFormData={setFormData}
        validationResults={validationResults}
        setValidationResults={setValidationResults}
      />
      <div
        id="customer-info"
        className="flex flex-col md:flex-row justify-between"
      >
        <CustomerSection
          customer={customer}
          handleSetCustomer={handleSetCustomer}
          validationResults={validationResults}
          formData={formData}
          // setFormData={setFormData}
          setValidationResults={setValidationResults}
        />
        <div className="py-6 w-[468px]">
          {isRetail ? (
            ""
          ) : (
            <ServiceType
              serviceType={serviceType}
              setServiceType={setServiceType}
            />
          )}
        </div>
      </div>
      {isRetail ? (
        <LineItemsFreeform
          formData={formData}
          setFormData={setFormData}
          variations={[]}
          total={selectedInventory?.total || 0}
        />
      ) : (
        <ServiceFreeformLineItem
          formData={formData}
          setFormData={setFormData}
          serviceType={serviceType as "time-based" | "session-based"}
        />
      )}
      <div id="line-table" className="mt-[3em] hidden md:block">
        <FreeformTable
          items={items}
          addItem={addItem}
          setItems={setItems}
          removeItem={removeItem}
          updateCell={updateCell}
          updateRow={updateRow}
          formData={formData}
          setFormData={setFormData}
          selectedCurrencyLabel={selectedCurrencyLabel}
          validationResults={validationResults}
          setValidationResults={setValidationResults}
          serviceType={serviceType as "time-based" | "session-based"}
        />
      </div>
      <div className="flex justify-between items-end">
        <div className="flex-1 md:block hidden">
          <AdditionalDetailsSection
            formData={formData}
            handleInputChange={handleInputChange}
          />
        </div>
        <div className="flex-1 justify-end ">
          <div className="ml-auto max-w-[500px]">
            <SummarySection
              subTotal={subTotal}
              formData={formData}
              calcDiscount={calcDiscount}
              calcTax={calcTax}
              total={total}
              calcShipping={calcShipping}
              handleInputChange={handleInputChange}
              isRetail={isRetail}
            />
          </div>
        </div>
      </div>
      <hr className="my-5" />
      <div className="hidden md:flex justify-between items-end">
        <div>
          <AddProjectButton error="" />
          <div id="add-bank">
            <BankSection />
          </div>
        </div>
        <RecurringSection formData={formData} setFormData={setFormData} />
      </div>
      <hr className="my-5" />
      <div>
        <div className="flex md:hidden">
          <AdditionalDetailsSection
            formData={formData}
            handleInputChange={handleInputChange}
          />
        </div>
      </div>
      <hr className="my-5 mt-8 md:hidden " />
      <div className="md:hidden">
        <AddProjectButton error="" />
        <BankSection />
        <hr className="my-5 md:hidden " />
        <RecurringSection formData={formData} setFormData={setFormData} />
      </div>
      {/* <RecurringSection /> */}
      <hr className="my-5 md:hidden" />
      {/* <ActionButtons /> */}
      <div className="w-full py-5 md:flex hidden justify-between lg:justify-end gap-[1em]">
        <ButtonCTA
          variant="outline"
          onClick={onSaveDraft}
          loading={isDraftLoading}
          disabled={isDraftLoading || !canSubmit}
        >
          Save as Draft
        </ButtonCTA>

        <ButtonCTA onClick={handleCreateInvoice} disabled={!canSubmit}>
          Create
        </ButtonCTA>
      </div>
      <div className="w-full py-5 flex md:hidden justify-between lg:justify-end gap-[1em]">
        <Button
          outline={true}
          variant="secondary"
          text={"Save as Draft"}
          size="small"
          font="semibold"
          onClick={onSaveDraft}
          isLoading={isDraftLoading}
          disabled={
            isDraftLoading ||
            (isRetail
              ? formData?.lineItems?.length == 0
              : formData?.services.filter((i: any) => i.id)?.length == 0)
          }
        />
        <div className="flex ">
          <Button
            variant="secondary"
            text={"Create"}
            size="small"
            font="semibold"
            onClick={handleCreateInvoice}
            disabled={
              isRetail
                ? formData?.lineItems?.length == 0
                : formData?.services.filter((i: any) => i.id)?.length == 0
            }
          />
        </div>
      </div>
      {showSidebar && (
        <SendInvoiceModal
          onCreateInvoice={onCreateInvoice}
          onClose={() => setShowSidebar(false)}
          formData={formData}
          validationResults={validationResults}
          handlePaymentDropDownChange={handlePaymentDropDownChange}
          markAsPaid={markAsPaid}
          handleMarkAsPaidChange={handleMarkAsPaidChange}
          handleSendEmailChange={handleSendEmailChange}
          shouldSendSMS={shouldSendSMS}
          customer={customer}
          sendEmail={sendEmail}
          handleshouldSendWhatsAppChange={handleshouldSendWhatsAppChange}
          handleInputChange={handleInputChange}
          handleshouldSendSMSChange={handleshouldSendSMSChange}
          isLoading={isLoading}
          shouldSendWhatsApp={shouldSendWhatsApp}
        />
      )}
      <SelectProjectModal /> <CreateProjectSideBar />
    </div>
  );
};

export default InvoiceFreeformContainer;
